import { useCallback, useRef, useState } from 'react';
import {
  navigateToDestination,
  navigateToSiteDetails,
  NavigationLike,
  resolveSiteDetailsDestination,
} from './navigateToSiteDetails';
import Layout from '../../constants/Layout';
import { Site } from '../../types/Site';
import { logAnalyticsEvent } from '../../utils/logAnalyticsEvent';

export interface UseSiteSelectionResult {
  selectedSite: Site | null;
  handleSelectSite: (site: Site) => void;
  handleSelectSiteFromList: (site: Site) => void;
  dismissSelection: () => void;
}

// Narrow layout, map marker tap: show a preview first (mirrors PantryMap.js, which doesn't
// special-case by data source either -- the preview's "View details" tap-through is what
// branches). Narrow layout, list tap: navigate straight to details -- the list row already shows
// everything the preview card would (mirrors PantriesScreen.jsx's own list, which does the same).
// Wide layout (either entry point): only a FreshTrak site has anything to show inline; a legacy
// site is ALSO handed to onSelectLegacyPantry (PantriesScreen.jsx's own selectPantry, showing the
// same PantryDetails Modal the legacy path uses over the map/list area) when given -- but
// selectedSite is still set for it too, same as any other site, so its marker highlights and a
// map-background tap (dismissSelection) both un-highlights it and closes the Modal via
// onDismissLegacyPantry. Without a callback (no caller currently omits it), a legacy site
// navigates away directly instead, matching the original (pre-Modal-reuse) behavior.
export function useSiteSelection(
  navigation: NavigationLike,
  onSelectLegacyPantry?: (organizationId: string) => void,
  onDismissLegacyPantry?: () => void
): UseSiteSelectionResult {
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  // Tracks whether onSelectLegacyPantry's Modal is currently open -- it's owned by
  // PantriesScreen.jsx's own selectedPantryId state, entirely separate from selectedSite here, so
  // switching straight from a legacy site to a FreshTrak one (without an intervening
  // dismissSelection) would otherwise leave that Modal open, covering the new SiteDetailsView.
  const legacyModalOpenRef = useRef(false);

  const handleSelectSite = useCallback(
    (site: Site) => {
      // Resolved once here (rather than also inside navigateToSiteDetails) so a malformed legacy
      // site's missing-organizationId fallback only logs once per selection.
      const destination = resolveSiteDetailsDestination(site);
      // Mirrors PantryMap.js's showPantryDetails, which logs on every marker press regardless of
      // narrow/wide layout -- organizationId is only meaningful for a legacyPantry destination.
      logAnalyticsEvent('pantry_preview', {
        siteId: site.siteId,
        organizationId: destination.kind === 'legacyPantry' ? destination.organizationId : undefined,
      });

      if (Layout.usePantryTabs) {
        setSelectedSite(site);
        return;
      }

      if (destination.kind === 'legacyPantry') {
        if (onSelectLegacyPantry) {
          onSelectLegacyPantry(destination.organizationId);
          legacyModalOpenRef.current = true;
        } else {
          navigateToDestination(navigation, destination);
          return;
        }
      } else if (legacyModalOpenRef.current) {
        onDismissLegacyPantry?.();
        legacyModalOpenRef.current = false;
      }
      setSelectedSite(site);
    },
    [navigation, onSelectLegacyPantry, onDismissLegacyPantry]
  );

  const handleSelectSiteFromList = useCallback(
    (site: Site) => {
      if (Layout.usePantryTabs) {
        navigateToSiteDetails(navigation, site);
        return;
      }
      handleSelectSite(site);
    },
    [navigation, handleSelectSite]
  );

  const dismissSelection = useCallback(() => {
    onDismissLegacyPantry?.();
    legacyModalOpenRef.current = false;
    setSelectedSite(null);
  }, [onDismissLegacyPantry]);

  return { selectedSite, handleSelectSite, handleSelectSiteFromList, dismissSelection };
}
